// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import ListView from './components/ListView';
import CatalogView from './components/CatalogView';
import LoginScreen from './components/LoginScreen';
import Toast from './components/Toast';
import { useStores } from './hooks/useStores';
import { AuthProvider, useAuth } from './contexts/AuthContext';

function AuthenticatedApp({ currentView, showToast }) {
  const {
    lists,
    activeList,
    activeListId,
    updateActiveList,
    addList,
    updateList,
    deleteList,
    fetchLists,
  } = useStores();

  const handleListChange = useCallback((list) => {
    updateActiveList(list);
    showToast(`Switched to ${list.name}`, 'success');
  }, [updateActiveList, showToast]);

  const handleNewList = useCallback(async (newListName) => {
    const added = await addList(newListName);
    if (added) {
      showToast(`New list "${newListName}" created`, 'success');
    } else {
      showToast(`List "${newListName}" already exists`, 'warning');
    }
  }, [addList, showToast]);

  if (currentView === 'list') {
    return (
      <ListView
        showToast={showToast}
        lists={lists}
        activeList={activeList}
        activeListId={activeListId}
        onListChange={handleListChange}
        onNewList={handleNewList}
        onRenameList={updateList}
        onDeleteList={deleteList}
        onRefreshLists={fetchLists}
      />
    );
  }

  return (
    <CatalogView
      showToast={showToast}
      lists={lists}
      activeList={activeList}
      activeListId={activeListId}
      onListChange={handleListChange}
      onRefreshLists={fetchLists}
    />
  );
}

function AppContent() {
  const [currentView, setCurrentView] = useState(() => {
    const hash = window.location.hash;
    if (hash === '#catalog') return 'catalog';
    return 'list';
  });

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const { isLoading, isAuthenticated, clearSession } = useAuth();

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, show: false }));
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      clearSession();
      showToast('Session expired. Please log in again.', 'error');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [clearSession, showToast]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#list') {
        setCurrentView('list');
      } else if (hash === '#catalog') {
        setCurrentView('catalog');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-600 font-medium">Loading...</div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return <LoginScreen />;
    }

    return (
      <AuthenticatedApp
        currentView={currentView}
        showToast={showToast}
      />
    );
  };

  return (
    <div className="App">
      {renderBody()}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
